import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    MarkdownViewer,
    TextAreaInput,
    Switch,
    Card,
    Text
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DocsTable,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "MarkdownViewer";

const USAGE_CODE = `<MarkdownViewer
    content={"# Release notes\\n\\nNew **ToggleGroup** and [docs](https://ncore.nibgat.space)."}
    onLinkPress={(url) => openInApp(url)}
/>

<MarkdownViewer
    isShowImageCaption={false}
    content={chatMessage}
/>`;

const CUSTOM_CODE = `<MarkdownViewer
    customTextProps={{
        "## ": {
            numberOfLines: 1
        }
    }}
    variants={{
        "# ": "titleMediumSize",
        "<p>": "bodyMediumSize",
        "- ": "bodyMediumSize"
    }}
    blockquoteIconColor="high"
    content={content}
/>`;

const READING_CODE = `<MarkdownViewer
    content={article}
    isReading={true}
/>`;

const SAMPLE_IMAGE_DATA = [
    "iVBORw0KGgoAAAANSUhEUgAAAUAAAAC0CAIAAABqhmJGAAAJUUlEQVR42u2da3BU5RmAz2Hyh2kdx3E6nc7UP2a3KEJFxIKKQjYg",
    "t8Ek5EZALkkQ6gWp2nv507uDhWqtIEoSEiCX3VzrCEVkd60oIAhKSRCVP22nnd4vWjLTP/RHOo4NYbPn7Dnfeb/ve57JD2D27H7v",
    "e97nvO+ecM5xV27+jwMAelLkOpfIAoCuAk9wERiADgwACAwAjNAAdGAAQGAAYIQGADowAAIDACM0ANCBARAYAHQcoUkCAB0YABAY",
    "ADgLDUAHBgAEBgBGaACgAwMgMAAwQgMAHRgAgQGAERoA6MAAgMAAjNAAQAcGAAQGAEZoADowACAwADBCA9CBAQCBAYARGgDowAAI",
    "DAA8WgUA6MAAgMAAnIUGADowACAwACM0ANCBAQCBAYARGoAODAAIDACM0AB0YABAYABghAYAOjAAAgMAIzQA0IEBAIEBGKEBgA4M",
    "AAgMwAgNAHRgAFAoMAAwQgP4ZdG6SblfcGDXebLECA2yWLjuBq+G/3LXu+QNgSFKFqy7sXDnD+46RyYZoUEp8xsnB3sUONQ0RAdG",
    "YFDBvMabwjgivNI0iMAAIVLaOCXs48LhprOM0ADBU9IwVc0xItP8azowQJDMbfiiyiNFtvkMAgMEw5yGm9UfL15tfocRGqBQ7qqf",
    "FtVR47WWt+nAAP6ZXX9LtMeOIy2nERjAD3fWT5dwBHm95RQjtFAeH3h5a9k9qAI5sOS7oXt0R7deK35s4NDIH7aVzadMpTFr7Qw5",
    "izm2+6T5Ah/bkdJlrY8OvDLqX35aNg9n5DBz7W3SlnR89wlGaBFs6j98JaWfLi9FHrBzkHZPPNclf5WP9Kdzv+Bn5QmKNVpmrJkp",
    "c2EnW49zFjoyNvZn8jT8mfISLIIxepTRv2cRPUI/1Jf1pPqzFXOpV7BqinZP72yXubIH+171t+H2ijlUrWKmrb5D8vLebnuDEVod",
    "D/T9qkDzd1TcjVRgwxQtboTe0PtaIIeAncvuonDB+Cl6gutckvMTiL0fHwhEhaYyh4o/ceqq2cKrfOqq2abu7iJXxsHp/p7XA3/P",
    "9b1HHMd5ofJOe1rNSBrX9x6xKuq8pmhDm3DRBAFfDxp73gi1ppsq7zC+QEfl0JKoPYyahn4Njv4kVkPPUQXF3Vx5u8HVOWYOR5Q2",
    "O3DOYxW50T1bpb77qMoSb6kysJTHzaGpgXsfoR1G6CBZ030sklpvrZplzM7LM4f13UdNipoROvoRenX38QiLvq1qpu67zWsCzYia",
    "EXrMEVppYKtSb0qo/j3VX9J3n/nLoe5RcxY6+hF6ZeqEHAf2Vd+m3d4qMIGaRs0ILWKEXpE6KVCG9uoZuuyqQBKoXdSM0NGP0HXJ",
    "tyRb0VFzq/CdFHgCtYiaETqvuH7TvDPUD6hNanBzwK6a6WLXFl4CA4z6uroFkvfvbzsOOlyN5IOapB63561NnkrW3CJtVWFnT2bU",
    "zM8iRujqLs3ujj9iS6p2mpD1qElgTfK0nJCZn/2E9vuW7YG/aWWXxs+n6am9OdoFRJK9wqP+3PLFMnfoHzr3OyZfKRn09U1a2zvi",
    "T4RXh0WVvcKjljw/G/wT5Ahd0WnIA1qXdZ3pWz5V8YdGnr1IomZ+LjS6P+1+JpA3Kus08BHpA8unqPkgOdkrJOTP1C6Vtgf/3PWi",
    "w8PNxuXezkEjs1PWefYXy28K9SOkpU5ByJx/DjLAv7Q+Xcj2SzuGHNN5sW5ySO8sNnu+Q762pkxOFH9NDhhfnO7fWp/yvfGSjnOO",
    "HbxUd2Owb6hF6vxFfU1NuYTF/z3Z7/B84Bws7njXsYYR3/bX3RDIu+mSuiUd54IKmeE5rDD/0bbN6zaL2s87tnJgxaRCNtcxdT5C",
    "vrp6WbRr/meq15KCdP/VttXTBgva33Ps5uCKL/jbUN/U+Qj5qurKqFb7YarHnmp0P2z7Sf6vvqf9fQcc5+UVcU+vNyBvXkN2HOfT",
    "1VXq1/lRqtuqUnQ/2vNkPq+bv+8DvB3FoZUx21KXZ8gf86mqapXL+3d3yrYidC/u2TLui0r3XUDXMTm8sti2vOUO+XImVtWoWdhw",
    "d9Kx8U4j2FsAOZJjat68xqXGKzvtdRzHvbj3ih24dC/q5teU7iu2LW+jQs6rFVeG0oqHe5I21557cd/YApfuwV6PNb2q2Kq8jcTr",
    "zeFlATs83Ju0vOrci+1jCFzapnEV/vF35z/7+UkcUFQ4vLrYx1YTKwLQeLgveHVL2y74iyhSgTv+T+DSVr3V/eRf0ViRxmt8Fv3E",
    "cs8mD/eH0nI/Wfa+w4leYE3tHeXt5WCyWIfzNDkkb3OUvS4au8Od/xM4sfuCeeqisTLSa4u1W3PumtciIne4a0uixXB1MVmRw/U6",
    "OZxn2QsPyr194QZLvEVjBWcH0w0aOJxo9taxJAeljcABqovGhWc+d97EVrxXdeUHpYHAIamLyUFlfszUpRtj4uxtKvQ/pQsMSq7A",
    "arxF4wAzPyp76XVSyj2xK8jrSeTEJVTgSNTF5DDOL6Tvj77WEy8EfzWYhLgkCixBXds0Djvng99dYpK60jQWIbA0by3RWHHaB7+n",
    "zuTE84quwU6vj1ktsHB1jTRZQs4Hvx+izImdqu+gkN4Qs05gvdQ1QGOZCR/8QZAmJ56L7OYn6S/HrBBYX281NVmXhA/+sFCTEzui",
    "v3VR+oGYsQKbpK4WGuubcK8yS1A3KodVCGyqujI1NinbQz8ax+SS7UJvGJh5MKa9wDZ4K8dks7M99OMxTC55VvTtPjMPxXQV2E51",
    "I9HYwlQPPbGk5Ofa3Kk383BMJ4FRV5nJpFqXXweE6nAwAlNMykqNVGsqc2ZjTKLA1JOaIiPPBpiceSQmSGBKSkGRkWTzTM5sikUp",
    "MCWlpsLIs8EyB+iwB4EpKQXlRZLtMTnzlZgigakqwORQHH40FqLAeAvIrELjx2IBC4y6gMlaODxaYNQFiErm7ONxnwLjLYAEk7Nf",
    "9eawe/2UuewkAFEmZ78WR2AAjWXOfj2OwAB6mzyuxggMINrk7DfiCAygt8zZb8YRGEBjk7PfiiMwgN4yj9IYgQE0Mzn77TgCA+ht",
    "cvY7cQQG0Fjm7OY4AgNobHIRKQNw5D1PI0+TERhA+pNxcsiMwAAat2UEBtC4LSMwgMYyTyARAPqCwAAIDAAIDAAIDIDAAIDAAIDA",
    "AIDAAAgMAAgMAAgMgMAAgMAAoJT/AuJa8SUiODjoAAAAAElFTkSuQmCC"
].join("");

const SAMPLE_IMAGE_URI = `data:image/png;base64,${SAMPLE_IMAGE_DATA}`;

const COMPACT_VARIANTS = {
    "# ": "titleMediumSize",
    "<p>": "bodyMediumSize",
    "- ": "bodyMediumSize"
} as const;

const CELL_STYLE: CSSProperties = {
    flexDirection: "column",
    display: "flex",
    gap: 8
};

const READING_STYLE: CSSProperties = {
    width: "100%"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("markdownViewerPage.syntaxTitle"),
        id: "syntax"
    },
    {
        title: t("markdownViewerPage.linksTitle"),
        id: "links"
    },
    {
        title: t("markdownViewerPage.imagesTitle"),
        id: "images"
    },
    {
        title: t("markdownViewerPage.readingTitle"),
        id: "reading"
    },
    {
        title: t("markdownViewerPage.customTitle"),
        id: "custom"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const MarkdownViewerPage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const [
        editedContent,
        setEditedContent
    ] = useState<string | undefined>(undefined);

    const [
        lastUrl,
        setLastUrl
    ] = useState("—");

    const [
        isShowImageCaption,
        setIsShowImageCaption
    ] = useState(true);

    const [
        isReading,
        setIsReading
    ] = useState(true);

    const overviewContent = editedContent ?? [
        `## ${t("markdownViewerPage.sampleHeading")}`,
        "",
        t("markdownViewerPage.sampleIntro"),
        "",
        `- ${t("markdownViewerPage.sampleItem1")}`,
        `- ${t("markdownViewerPage.sampleItem2")}`,
        "",
        `> ${t("markdownViewerPage.sampleQuote")}`
    ].join("\n");

    const formatsContent = [
        `# ${t("sample.title")}`,
        "",
        `## ${t("sample.subtitle")}`,
        "",
        t("sample.markdown"),
        "",
        `- ${t("sample.item1")}`,
        `    - ${t("sample.item2")}`,
        "",
        `1. ${t("sample.option1")}`,
        `2. ${t("sample.option2")}`,
        "",
        `> ${t("sample.body")}`,
        "",
        "---",
        "",
        "```typescript",
        t("sample.code"),
        "```"
    ].join("\n");

    const linksContent = t("markdownViewerPage.linksSample");

    const imageContent = `![${t("markdownViewerPage.imageAlt")}](${SAMPLE_IMAGE_URI} "${t("markdownViewerPage.imageTitle")}")`;

    const customContent = [
        `# ${t("sample.title")}`,
        "",
        t("sample.body"),
        "",
        `- ${t("sample.item1")}`,
        `- ${t("sample.item2")}`
    ].join("\n");

    const readingContent = [
        `## ${t("markdownViewerPage.readingSampleHeading")}`,
        "",
        t("markdownViewerPage.readingSampleParagraph1"),
        "",
        t("markdownViewerPage.readingSampleParagraph2"),
        "",
        `- ${t("markdownViewerPage.readingSampleItem1")}`,
        `- ${t("markdownViewerPage.readingSampleItem2")}`,
        "",
        `> ${t("markdownViewerPage.readingSampleQuote")}`
    ].join("\n");

    const handleLinkPress = (url: string) => setLastUrl(url);

    const handleReadingToggle = () => setIsReading(!isReading);

    const handleImageCaptionToggle = () => setIsShowImageCaption(!isShowImageCaption);

    const renderCode = (value: string) => <InlineCode>
        {value}
    </InlineCode>;

    const renderSurface = (node: ReactNode) => <Card>
        <Card.Content>
            {node}
        </Card.Content>
    </Card>;

    const renderCell = (label: string, node: ReactNode) => <div
        style={CELL_STYLE}
        key={label}
    >
        <Text
            variant="labelSmallSize"
            color="mid"
        >
            {label}
        </Text>
        {node}
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("markdownViewerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("markdownViewerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("markdownViewerPage.overviewCaption")}
                isStacked={true}
            >
                <TextAreaInput
                    onChangeText={setEditedContent}
                    initialValue={overviewContent}
                    isUpdateOnRealtime={true}
                    title="Markdown"
                    key={language}
                />
                {
                    renderSurface(<MarkdownViewer
                        content={overviewContent}
                    />)
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("markdownViewerPage.syntaxTitle")}
            id="syntax"
        >
            <Paragraph>
                {t("markdownViewerPage.syntaxText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderSurface(<MarkdownViewer
                        content={formatsContent}
                    />)
                }
            </Preview>
            <CodeBlock
                code={formatsContent}
                language="markdown"
            />
            <DocsTable
                rows={[
                    [
                        renderCode("# … ######"),
                        t("markdownViewerPage.syntaxHeadings")
                    ],
                    [
                        renderCode("**b**  *i*  ~~s~~  <u>u</u>"),
                        t("markdownViewerPage.syntaxEmphasis")
                    ],
                    [
                        renderCode("`code`"),
                        t("markdownViewerPage.syntaxInlineCode")
                    ],
                    [
                        renderCode("```ts"),
                        t("markdownViewerPage.syntaxCodeBlock")
                    ],
                    [
                        renderCode("-  *  +  1."),
                        t("markdownViewerPage.syntaxLists")
                    ],
                    [
                        renderCode(">"),
                        t("markdownViewerPage.syntaxQuote")
                    ],
                    [
                        renderCode("---"),
                        t("markdownViewerPage.syntaxRule")
                    ],
                    [
                        renderCode("[text](url)  <url>  https://…"),
                        t("markdownViewerPage.syntaxLinks")
                    ],
                    [
                        renderCode("![alt](url \"title\")"),
                        t("markdownViewerPage.syntaxImages")
                    ]
                ]}
                headers={[
                    t("markdownViewerPage.tableSyntax"),
                    t("markdownViewerPage.tableResult")
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("markdownViewerPage.syntaxNote")}
            </Paragraph>
        </Section>
        <Section
            title={t("markdownViewerPage.linksTitle")}
            id="links"
        >
            <Paragraph>
                {t("markdownViewerPage.linksText")}
            </Paragraph>
            <Preview
                caption={t("markdownViewerPage.linksCaption", [
                    lastUrl
                ])}
                isStacked={true}
            >
                {
                    renderSurface(<MarkdownViewer
                        onLinkPress={handleLinkPress}
                        content={linksContent}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("markdownViewerPage.imagesTitle")}
            id="images"
        >
            <Paragraph>
                {t("markdownViewerPage.imagesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderSurface(<MarkdownViewer
                        isShowImageCaption={isShowImageCaption}
                        content={imageContent}
                    />)
                }
                <Switch
                    onPress={handleImageCaptionToggle}
                    isChecked={isShowImageCaption}
                    title="isShowImageCaption"
                />
            </Preview>
        </Section>
        <Section
            title={t("markdownViewerPage.readingTitle")}
            id="reading"
        >
            <Paragraph>
                {t("markdownViewerPage.readingText")}
            </Paragraph>
            <Preview
                caption={t("markdownViewerPage.readingCaption")}
            >
                <div
                    style={READING_STYLE}
                >
                    <Card
                        spreadBehaviour="stretch"
                    >
                        <Card.Content>
                            <MarkdownViewer
                                content={readingContent}
                                isReading={isReading}
                            />
                        </Card.Content>
                    </Card>
                </div>
                <Switch
                    onPress={handleReadingToggle}
                    isChecked={isReading}
                    title="isReading"
                />
            </Preview>
            <CodeBlock
                code={READING_CODE}
                language="tsx"
            />
            <BulletList
                items={[
                    t("markdownViewerPage.readingBody"),
                    t("markdownViewerPage.readingHeadings"),
                    t("markdownViewerPage.readingMeasure"),
                    t("markdownViewerPage.readingEmphasis"),
                    t("markdownViewerPage.readingArabic")
                ]}
            />
        </Section>
        <Section
            title={t("markdownViewerPage.customTitle")}
            id="custom"
        >
            <Paragraph>
                {t("markdownViewerPage.customText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    renderCell("variants", renderSurface(<MarkdownViewer
                        variants={COMPACT_VARIANTS}
                        content={customContent}
                    />))
                }
                {
                    renderCell(t("markdownViewerPage.defaultLabel"), renderSurface(<MarkdownViewer
                        content={customContent}
                    />))
                }
            </Preview>
            <CodeBlock
                code={CUSTOM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("markdownViewerPage.a11yHeadings"),
                    t("markdownViewerPage.a11yLists"),
                    t("markdownViewerPage.a11yLinks"),
                    t("markdownViewerPage.a11yImages"),
                    t("markdownViewerPage.a11yCode"),
                    t("markdownViewerPage.a11yQuote"),
                    t("markdownViewerPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("markdownViewerPage.propContent"),
                        name: "content",
                        type: "string"
                    },
                    {
                        description: t("markdownViewerPage.propSyntax"),
                        type: "\"standard\" | \"legacy\"",
                        defaultValue: "\"standard\"",
                        name: "syntax"
                    },
                    {
                        description: t("markdownViewerPage.propOnLinkPress"),
                        type: "(url: string) => void",
                        name: "onLinkPress"
                    },
                    {
                        description: t("markdownViewerPage.propIsShowImageCaption"),
                        name: "isShowImageCaption",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("markdownViewerPage.propIsReading"),
                        defaultValue: "false",
                        name: "isReading",
                        type: "boolean"
                    },
                    {
                        description: t("markdownViewerPage.propImageProps"),
                        type: "ImageProps",
                        name: "imageProps"
                    },
                    {
                        description: t("markdownViewerPage.propVariants"),
                        type: "MarkdownVariantsType",
                        name: "variants"
                    },
                    {
                        description: t("markdownViewerPage.propCustomTextProps"),
                        type: "Partial<Record<TextMarkdownTypes, TextProps>>",
                        name: "customTextProps"
                    },
                    {
                        description: t("markdownViewerPage.propCustomTextStyles"),
                        type: "Partial<Record<TextMarkdownTypes, TextStyle>>",
                        name: "customTextStyles"
                    },
                    {
                        description: t("markdownViewerPage.propBoldStyle"),
                        type: "TextStyle",
                        name: "boldStyle"
                    },
                    {
                        description: t("markdownViewerPage.propItalicStyle"),
                        name: "italicStyle",
                        type: "TextStyle"
                    },
                    {
                        description: t("markdownViewerPage.propListTextProps"),
                        name: "listTextProps",
                        type: "TextProps"
                    },
                    {
                        description: t("markdownViewerPage.propListIconProps"),
                        name: "listIconProps",
                        type: "TextProps"
                    },
                    {
                        description: t("markdownViewerPage.propBlockquoteTextColor"),
                        type: "keyof TextContentColors",
                        name: "blockquoteTextColor",
                        defaultValue: "\"high\""
                    },
                    {
                        description: t("markdownViewerPage.propBlockquoteIconColor"),
                        type: "keyof IconContentColors",
                        name: "blockquoteIconColor",
                        defaultValue: "\"mid\""
                    },
                    {
                        description: t("markdownViewerPage.propBlockquoteIconSize"),
                        name: "blockquoteIconSize",
                        type: "keyof Typography"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("markdownViewerPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("markdownViewerPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("markdownViewerPage.propsNote")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("markdownViewerPage.legacyNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default MarkdownViewerPage;
